import { DEFAULT_LOGO_URL } from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Alert,
  Box,
  Button,
  Heading,
  IconArrow,
  Spread,
  Stack,
  Text,
} from 'braid-design-system';
import { ExperimentTwoProgress } from '../../type';
import translations from './.vocab';
import * as styles from './ReviewPageLayout.css';

type ReviewPageLayoutProps = React.PropsWithChildren<{
  alertText?: string;
  heading?: string;
  hideStepper?: boolean;
  isContinueLoading?: boolean;
  logo?: string;
  onAlertClosed?: () => void;
  onContinue?: () => void;
  onSkip?: () => void;
  progress?: ExperimentTwoProgress;
  subtitle?: string;
  testid?: string;
}>;

interface DesktopButtonProps {
  isContinueLoading: boolean;
  isLastStep: boolean;
  onContinue?: () => void;
  onSkip?: () => void;
}

interface AlertBoxProps {
  alertText?: string;
  onAlertClosed?: () => void;
}

const DesktopButton = ({
  onSkip,
  onContinue,
  isLastStep,
  isContinueLoading,
}: DesktopButtonProps) => {
  return (
    <Spread space="large">
      <Actions>
        <Button
          variant="transparent"
          tone="neutral"
          icon={<IconArrow direction="right" />}
          iconPosition="trailing"
          onClick={onSkip}
          data={{ testid: 'skip-button' }}
        >
          Skip question
          {/* TODO: Translation */}
        </Button>
      </Actions>
      <Actions>
        {onContinue ? (
          <Button
            variant="solid"
            tone={!isLastStep ? 'formAccent' : 'brandAccent'}
            onClick={onContinue}
            data={{ testid: 'save-and-continue-button' }}
            loading={isContinueLoading}
          >
            Save and continue
            {/* {!isLastStep ? 'Save and continue' : t('Post review')} */}
          </Button>
        ) : null}
      </Actions>
    </Spread>
  );
};

const AlertBox = ({ onAlertClosed, alertText }: AlertBoxProps) => {
  if (!onAlertClosed || !alertText) {
    return null;
  }
  return (
    <Alert
      tone="critical"
      onClose={onAlertClosed}
      closeLabel="Close critical alert"
    >
      <Text>{alertText}</Text>
    </Alert>
  );
};

export const ReviewPageLayoutNoStepper = ({
  progress = 1,
  heading,
  subtitle,
  logo,
  children,
  testid,
  alertText,
  onContinue,
  onSkip,
  onAlertClosed,
  isContinueLoading = false,
  hideStepper = false,
}: ReviewPageLayoutProps) => {
  const { t } = useTranslations(translations);
  const isLastStep = progress === ExperimentTwoProgress.WorkingEnvironment;

  return (
    <Box
      data={testid ? { testid } : {}}
      paddingTop={{
        tablet: 'xxlarge',
        mobile: 'large',
      }}
    >
      <Stack space="xlarge">
        {logo && logo !== DEFAULT_LOGO_URL ? (
          <Box>
            <Box
              component="img"
              src={logo}
              alt={t('company logo')}
              className={styles.logoBox}
            />
          </Box>
        ) : null}
        <Stack space="large">
          {!hideStepper && (
            <Text align="right" tone="secondary">
              {progress - 1} of 7
            </Text>
          )}
          <Heading level="1">{heading}</Heading>
        </Stack>
        <AlertBox onAlertClosed={onAlertClosed} alertText={alertText} />
        <Box className={styles.questionsBox}>
          <Stack space="xlarge">
            {subtitle ? <Text size="standard">{subtitle}</Text> : null}
            <Box>{children}</Box>
          </Stack>
        </Box>
        {progress === 1 && (
          <Button
            tone="brandAccent"
            variant="solid"
            onClick={onContinue}
            loading={isContinueLoading}
          >
            Submit
          </Button>
        )}
        {progress !== 1 && (
          <DesktopButton
            isLastStep={isLastStep}
            isContinueLoading={isContinueLoading}
            onSkip={onSkip}
            onContinue={onContinue}
          />
        )}
      </Stack>
    </Box>
  );
};
